import { FC, useEffect, useMemo, useState } from "react";
import { TextField } from "@mui/material";
import BasicSelect from "../../components/BasicSelect";
import { useGroupLabels } from "../../hooks/groupLabels";
import { ValidQlikOptions } from "../../domain/report.interface";
import { Program } from "../../domain/beneficiary.interface";
import { useUserStore } from "../../stores/user.store";
import {
	useGetProgramsByIds,
	useGetProviderSitesByOUId,
	useGetProvidersByProgramId,
} from "../../hooks/useBeneficiaries";
import { useSettings } from "../../hooks/useSetting";
import { useBeneficiaryTerm } from "../../hooks/useBeneficiaryTerm";
import {
	canRunReport,
	combinedOrgUnitIds,
	defaultProgramSelection,
} from "../../utils/programSelection";

export type ReportFiltersProps = {
	programId: string[];
	providerSiteId: string[];
	providerId: string;
	providerNpi: string;
	beneficiary: string;
	beneficiaryCin: string;
};

type Props = {
	filters: ReportFiltersProps;
	onFiltersChange: (newFilters: ReportFiltersProps) => void;
	showBeneficiaryFilters?: boolean;
	validOptions?: ValidQlikOptions;
};

export const ReportFilters: FC<Props> = ({
	filters,
	onFiltersChange,
	showBeneficiaryFilters = true,
	validOptions,
}) => {
	const { userInfo } = useUserStore();
	// const queryClient = useQueryClient();
	const { data: settingsV2 } = useSettings(userInfo?.id);
	const { singular } = useBeneficiaryTerm();

	const userPrograms =
		userInfo?.programs.map((program) => program.programId) || [];

	const [programsOptions, setProgramsOptions] = useState<
		Array<{ key: string; value: string }>
	>([]);
	const [providerOptions, setProviderOptions] = useState<
		Array<{ key: string; value: string }>
	>([]);

	// Initialize OUIs based on the current filters.programId (for GO BACK scenario)
	// or fall back to the first program's OUIs (for initial load)
	const initialOuIds = useMemo(() => {
		if (!userInfo?.programs || userInfo.programs.length === 0) return [];

		// If filters already have a programId, use that program's OUIs
		if (
			filters.programId &&
			filters.programId.length > 0 &&
			filters.programId[0] !== ""
		) {
			const matchingPrograms = userInfo.programs.filter((p) =>
				filters.programId.includes(p.programId),
			);
			if (matchingPrograms.length > 0) {
				return matchingPrograms.flatMap((p) =>
					p.orgUnitId.map((o) => (typeof o === "string" ? o : o.orgUnitId)),
				);
			}
		}

		// Fall back to first program's OUIs
		const firstProgram = userInfo.programs[0];
		return firstProgram.orgUnitId.map((o) =>
			typeof o === "string" ? o : o.orgUnitId,
		);
	}, [userInfo, filters.programId]);

	const [programOuiSelected, setProgramOuiSelected] =
		useState<string[]>(initialOuIds);
	type ProviderSiteWithLabels = {
		key: string;
		id: string;
		name: string;
		value: string;
		groupLabelIds?: string[];
	};

	const [filteredProviderSitesData, setFilteredProviderSitesData] = useState<
		ProviderSiteWithLabels[]
	>([]);
	const [selectedProviderSites, setSelectedProviderSites] = useState<string[]>(
		filters.providerSiteId || [],
	);
	const [selectedGroupLabels, setSelectedGroupLabels] = useState<string[]>([]);
	const [selectedProgram, setSelectedProgram] = useState<Program | undefined>();
	const { data: groupLabels = [], isLoading: isLoadingGroupLabels } =
		useGroupLabels();

	// programs
	const { data: programsData, isLoading: isLoadingPrograms } =
		useGetProgramsByIds(userPrograms);

	// provider sites
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { data: providerSitesData, refetch: refetchProviderSites } =
		useGetProviderSitesByOUId({
			ouIds: programOuiSelected || [],
			queryKey: "sims-beneficiaries-list-provider-sites",
		});

	useEffect(() => {
		if (
			!providerSitesData ||
			!filters.programId ||
			filters.programId.length === 0
		) {
			setFilteredProviderSitesData([]);
			return;
		}
		setFilteredProviderSitesData(providerSitesData);

		// Only auto-select all OUs if no provider sites are currently selected
		// This preserves the selection when the component remounts (e.g., after GO BACK)
		if (filters.providerSiteId.length === 0) {
			const allOuIds = providerSitesData.map((ou) => ou.id);
			setSelectedProviderSites(allOuIds);
			onFiltersChange({ ...filters, providerSiteId: allOuIds });
		}
	}, [filters.programId, providerSitesData]);

	// providers - only fetch when we have valid program and provider site IDs
	const { isLoading: isLoadingProviders, refetch: refetchProviders } =
		useGetProvidersByProgramId(
			filters.programId.length > 0 && filters.programId[0] !== ""
				? filters.programId
				: [],
			filters.providerSiteId.length > 0 ? filters.providerSiteId : [],
			false,
		);

	const onProgramFilterChange = (value: string | string[]) => {
		// SIMS-7990 AC1: the program filter is multi-select, so MUI hands us an
		// array. The previous single-select form only handled the non-array case;
		// keeping both means the handler works either way and an empty selection
		// (AC3) is not silently dropped.
		const valueArray = Array.isArray(value)
			? value.flat()
			: value.includes(",")
				? value.split(",")
				: value === ""
					? []
					: [value];

		setFilteredProviderSitesData([]);

		// Org units are the COMBINED set across the selected programs, taken from the
		// user's own entitlements (FR-2/FR-4 client side; the server narrows too).
		// De-duplicated: an org unit shared by several selected programs is one
		// option, not one per program — otherwise the filter repeats it and the same
		// id is sent to the query more than once.
		const allOuIds = combinedOrgUnitIds(userInfo?.programs, valueArray);

		// Set OUI before updating filters to ensure proper sequencing
		setProgramOuiSelected(allOuIds);
		setProviderOptions([]);

		// Set selected program based on the first selected program ID
		const newSelectedProgram = programsData?.find((p) =>
			valueArray.includes(p._id),
		);
		setSelectedProgram(newSelectedProgram as unknown as Program);

		const newFilters = {
			...filters,
			programId: valueArray,
			providerId: "",
			providerSiteId: allOuIds,
		};
		setSelectedProviderSites(allOuIds);
		setSelectedGroupLabels([]);

		// Call onFiltersChange after state updates
		onFiltersChange(newFilters);
	};

	useEffect(() => {
		if (programsData && !isLoadingPrograms && programsData.length > 0) {
			const programOptions = programsData.map((program) => ({
				key: program._id,
				value: program.name,
			}));

			setProgramsOptions(programOptions);

			// Only set initial program if not already set
			if (filters.programId.length === 0 || filters.programId[0] === "") {
				// SIMS-7990 AC2: pre-select EVERY assigned program, not just the
				// first. Intersected with the options the server returned, so a
				// program the user is no longer entitled to cannot be pre-selected.
				const assignedIds = defaultProgramSelection(userInfo?.programs);
				const initialProgramIds = assignedIds.filter((programId) =>
					programsData.some((program) => program._id === programId),
				);

				// Combined + de-duplicated, same rule as onProgramFilterChange: with
				// every assigned program pre-selected (AC2), a shared org unit would
				// otherwise appear once per program on first load.
				const userProgramsOuis = combinedOrgUnitIds(
					userInfo?.programs,
					initialProgramIds,
				);

				// Set OUI first
				setProgramOuiSelected(userProgramsOuis);

				// Set initial selected program
				if (programsData[0]) {
					setSelectedProgram(programsData[0] as unknown as Program);
				}

				// Set initial selected provider sites to the OUIs
				setSelectedProviderSites(userProgramsOuis);

				const newFilters = {
					...filters,
					programId: initialProgramIds,
					providerId: "",
					providerSiteId: userProgramsOuis, // Set OUIs directly instead of empty array
				};
				onFiltersChange(newFilters);
				setProviderOptions([]);
			} else {
				// Set selected program based on current filter
				const currentProgram = programsData.find((p) =>
					filters.programId.includes(p._id),
				);
				if (currentProgram) {
					setSelectedProgram(currentProgram as unknown as Program);
				}
			}
		}
	}, [programsData, isLoadingPrograms]);

	// Note: No need to manually refetch provider sites - React Query handles it automatically
	// when programOuiSelected changes (it's in the query key)

	useEffect(() => {
		if (
			filters.programId.length > 0 &&
			filters.providerSiteId.length > 0 &&
			!isLoadingProviders
		) {
			refetchProviders().then((data) => {
				let filteredProviders = data?.data || [];

				// Filter providers by CM roles
				filteredProviders = filteredProviders.filter((p) =>
					p.roles?.some((r) => selectedProgram?.cmRoles?.includes(r)),
				);

				// Filter providers based on valid Qlik options if available
				if (validOptions?.providerIds) {
					filteredProviders = filteredProviders.filter((provider) =>
						validOptions?.providerIds?.includes(provider.value),
					);
				}

				setProviderOptions(filteredProviders);
			});
		}
	}, [isLoadingProviders, filters, validOptions, selectedProgram]);

	const groupLabelOptions = useMemo(() => {
		const availableLabels = providerSitesData?.flatMap(
			(ou) => ou.groupLabelIds || [],
		);
		return groupLabels
			.filter((l) => availableLabels?.includes(l._id))
			.map((label) => ({
				key: label._id,
				value: label.name,
			}));
	}, [groupLabels, providerSitesData]);

	return (
		<>
			{/*
			 * SIMS-7990: AC1 multi-select (more than one program, no maximum),
			 * AC3 an empty selection is allowed and surfaces the "Select a program"
			 * prompt instead of being blocked — note the org-unit select below
			 * deliberately re-selects all on empty, which must NOT happen here.
			 */}
			<BasicSelect
				handleChange={onProgramFilterChange}
				data={programsOptions || []}
				value={filters.programId}
				label="Select Program"
				id="report-program"
				placeholder="Select"
				isLoading={isLoadingPrograms}
				multiple
				required
				error={!canRunReport(filters.programId)}
				errorMessage="Select a program"
			/>

			<BasicSelect
				handleChange={(value) => {
					// Always flatten the value to a string[]
					let selected: string[] = [];
					if (Array.isArray(value)) {
						selected = value.flat();
					} else {
						selected = [value];
					}
					// Prevent unselecting all: if user tries to unselect all, re-select all
					if (selected.length === 0 && filteredProviderSitesData.length > 0) {
						selected = filteredProviderSitesData.map((ou) => ou.id);
					}
					setSelectedProviderSites(selected);
					setSelectedGroupLabels([]); // Deselect group labels when OU is selected
					const newFilters = {
						...filters,
						providerSiteId: selected,
						groupLabelIds: [],
						providerId: "",
					};
					onFiltersChange(newFilters);
					setProviderOptions([]);
				}}
				data={(filteredProviderSitesData || []).map((ou) => ({
					key: ou.id || ou.key,
					value: ou.name || ou.value,
				}))}
				value={selectedProviderSites}
				label="Select OU"
				id="report-ou"
				multiple
				isLoading={false}
			/>

			<BasicSelect
				handleChange={(value) => {
					const selectedLabels = Array.isArray(value) ? value : [value];
					setSelectedGroupLabels(selectedLabels);
					let selectedOuIds: string[];
					if (selectedLabels.length === 0) {
						// No labels selected: select all OUs
						selectedOuIds = filteredProviderSitesData.map((ou) => ou.id);
					} else {
						// Find OUs with any of the selected group labels
						const matchingOUs = (filteredProviderSitesData || []).filter(
							(ou) =>
								Array.isArray(ou.groupLabelIds) &&
								ou.groupLabelIds.some((labelId) =>
									selectedLabels.includes(labelId),
								),
						);
						selectedOuIds = matchingOUs.map((ou) => ou.id);
					}
					setSelectedProviderSites(selectedOuIds);
					const newFilters = {
						...filters,
						groupLabelIds: selectedLabels,
						providerSiteId: selectedOuIds,
						providerId: "",
					};
					onFiltersChange(newFilters);
					setProviderOptions([]);
				}}
				data={groupLabelOptions}
				value={selectedGroupLabels}
				label="Select Group Label"
				id="report-group-label"
				multiple
				isLoading={isLoadingGroupLabels}
			/>

			<BasicSelect
				handleChange={(value) => {
					const newFilters = {
						...filters,
						providerId: value as string,
					};
					onFiltersChange(newFilters);
				}}
				data={providerOptions}
				value={filters.providerId}
				disabled={
					filters.programId.includes("all") ||
					providerOptions.length === 0 ||
					isLoadingProviders
				}
				label="Select Provider"
				id="report-provider"
				placeholder="Select"
			/>
			<TextField
				label="Provider NPI"
				value={filters.providerNpi}
				onChange={(e) =>
					onFiltersChange({
						...filters,
						providerNpi: e.target.value,
					})
				}
			/>
			{showBeneficiaryFilters && (
				<>
					{settingsV2?.displayBeneficiaryName ? (
						<TextField
							label={singular}
							value={filters.beneficiary}
							onChange={(e) =>
								onFiltersChange({
									...filters,
									beneficiary: e.target.value,
								})
							}
						/>
					) : (
						<></>
					)}
					<TextField
						label={`${singular} CIN`}
						value={filters.beneficiaryCin}
						onChange={(e) =>
							onFiltersChange({
								...filters,
								beneficiaryCin: e.target.value,
							})
						}
					/>
				</>
			)}
		</>
	);
};

export default ReportFilters;
